import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Pages a signed-out visitor may be on. Anything else needs a session.
const PUBLIC_EXACT = new Set([
  "/",
  "/home",
  "/callback",
  "/security",
  "/hjalp",
  "/integritet",
  "/villkor",
  "/cookies",
  "/sakerhet-och-bedragerier",
  "/sakerhet-och-bedragerier-v1",
  "/en/security-and-fraud",
]);
const PUBLIC_PREFIXES = ["/user/login", "/user/register", "/user/buyerregister"];

export const isPublicPath = (pathname) => {
  const p = String(pathname || "/").toLowerCase().replace(/\/+$/, "") || "/";
  if (PUBLIC_EXACT.has(p)) return true;
  // "/Buyer" and "/Buyer/:encrypted" are the buyer's public entry; "/BuyerProfile" and
  // "/BuyerSuccess" are not.
  if (p === "/buyer" || p.startsWith("/buyer/")) return true;
  return PUBLIC_PREFIXES.some((x) => p.startsWith(x));
};

// The user signed out in another tab of this browser. The `storage` event fires in the OTHER tabs
// only, so this tab learns the token is gone without having done anything itself. Pages that are
// already open would otherwise stay on screen and every button would silently fail; send the user
// to the login page instead.
export default function SignedOutElsewhereGuard() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onStorage = (event) => {
      const removed = event.key === "authToken" ? !event.newValue : event.key === null;
      if (!removed) return;
      let stillSignedIn = false;
      try {
        stillSignedIn = Boolean(localStorage.getItem("authToken"));
      } catch {
        /* storage unavailable */
      }
      if (stillSignedIn || isPublicPath(window.location.pathname)) return;
      navigate("/Home", { replace: true });
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [navigate]);

  // Also covers a tab that was asleep and missed the event: the next navigation lands on login.
  useEffect(() => {
    let signedIn = true;
    try {
      signedIn = Boolean(localStorage.getItem("authToken"));
    } catch {
      /* storage unavailable */
    }
    if (!signedIn && !isPublicPath(pathname)) navigate("/Home", { replace: true });
  }, [pathname, navigate]);

  return null;
}
